import { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { spRequest } from '../authConfig';
import { updateWin, getAttachments, uploadAttachment, deleteAttachment } from '../graphService';
import RichTextEditor from './RichTextEditor';
import DropZone from './DropZone';
import '../styles/EditWinModal.css';

const DEPARTMENTS = [
  'Accounting', 'Bleu Cheese Crumbles', 'Bleu Cheese Wheels', 'Buttermilk',
  'Customer Support', 'Formulation', 'General Admin',
  'Human Resources', 'IT', 'Innovation', 'Inventory Control', 'Logistics',
  'Maintenance', 'Marketing', 'Operation Support', 'Packaging',
  'Planning & Scheduling', 'Plant General', 'Portion Control', 'Purchasing',
  'Quality Assurance', 'Quality Control', 'Regulatory', 'Research and Development',
  'Sales', 'Sanitation', 'Transportation', 'Truck Shop', 'Warehouse', 'Waste Water',
];

const FREQUENCIES = ['One-time', 'Daily', 'Weekly', 'Monthly'];
const IMAGE_EXTS = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;

function minsToHrsMins(total) {
  const mins = Math.round(Number(total) || 0);
  return { hrs: String(Math.floor(mins / 60) || ''), mins: String(mins % 60 || '') };
}

export default function EditWinModal({ win, onClose, onSaved }) {
  const { instance, accounts } = useMsal();
  const [form, setForm] = useState({});
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [removedAttachments, setRemovedAttachments] = useState([]); // names to delete on save
  const [newAttachments, setNewAttachments] = useState([]);          // File objects to upload
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const saved = minsToHrsMins(win.timeSavedMinutes);
    setForm({
      task: win.task || '',
      howDidYouUseAI: win.howDidYouUseAI || '',
      benefits: win.benefits || '',
      timeSavedHrs: saved.hrs,
      timeSavedMins: saved.mins,
      frequency: win.frequency || '',
      department: win.department || '',
      // Value types are independent — a win can have both capacity and a non-time benefit.
      valueCapacity: win.timeSavedMinutes > 0,
      valueBenefit: !!win.benefits,
    });
  }, [win]);

  // Fetch existing attachments
  useEffect(() => {
    async function load() {
      try {
        const tokenRes = await instance.acquireTokenSilent({ ...spRequest, account: accounts[0] });
        const files = await getAttachments(tokenRes.accessToken, win.id);
        setExistingAttachments(files);
      } catch {
        // silently ignore
      }
    }
    load();
  }, [win.id, instance, accounts]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const toggleCapacity = () => {
    setForm((f) => f.valueCapacity
      ? { ...f, valueCapacity: false, timeSavedHrs: '', timeSavedMins: '' }
      : { ...f, valueCapacity: true });
  };

  const toggleBenefit = () => {
    setForm((f) => f.valueBenefit
      ? { ...f, valueBenefit: false, benefits: '' }
      : { ...f, valueBenefit: true });
  };

  const handleRemoveExisting = (file) => {
    setRemovedAttachments((prev) => [...prev, file.name]);
    setExistingAttachments((prev) => prev.filter((f) => f.name !== file.name));
  };

  const handleRemoveNew = (index) => {
    setNewAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const timeSaved = form.valueCapacity
      ? (parseInt(form.timeSavedHrs) || 0) * 60 + (parseInt(form.timeSavedMins) || 0)
      : 0;
    const benefits = form.valueBenefit ? form.benefits : '';
    try {
      const tokenRes = await instance.acquireTokenSilent({ ...spRequest, account: accounts[0] });

      await updateWin(tokenRes.accessToken, win.id, {
        task: form.task,
        howDidYouUseAI: form.howDidYouUseAI,
        benefits,
        // "Time Spent Using AI" was retired from the form; preserve the original value.
        timeSpent: win.timeSpentMinutes,
        timeSaved,
        frequency: form.frequency,
        department: form.department,
      });

      // Delete removed attachments
      for (const name of removedAttachments) {
        await deleteAttachment(tokenRes.accessToken, win.id, name);
      }

      // Upload new attachments
      for (const file of newAttachments) {
        await uploadAttachment(tokenRes.accessToken, win.id, file);
      }

      onSaved({
        ...win,
        task: form.task,
        howDidYouUseAI: form.howDidYouUseAI,
        benefits,
        timeSpentMinutes: win.timeSpentMinutes,
        timeSavedMinutes: timeSaved,
        frequency: form.frequency,
        department: form.department,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="edit-backdrop" onClick={onClose}>
      <div className="edit-panel" onClick={(e) => e.stopPropagation()}>
        <div className="edit-panel-header">
          <h3 className="edit-title">Edit AI Win</h3>
          <button className="edit-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="edit-form">

          <div className="form-group">
            <label htmlFor="e-task">Task Completed *</label>
            <textarea id="e-task" name="task" rows={3} required value={form.task} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label>How Did You Use AI? *</label>
            <RichTextEditor
              value={form.howDidYouUseAI}
              onChange={(html) => setForm((f) => ({ ...f, howDidYouUseAI: html }))}
              placeholder="Enter your answer"
              rows={4}
            />
          </div>

          <div className="form-group">
            <label htmlFor="e-dept">Department *</label>
            <select id="e-dept" name="department" required value={form.department} onChange={handleChange}>
              <option value="">Select your department...</option>
              {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label>What value did this AI win create?</label>
            <p className="field-hint">Select all that apply — a win can free up capacity and deliver a non-time benefit.</p>
            <div className="value-type-toggle">
              <button
                type="button"
                className={`value-type-btn ${form.valueCapacity ? 'selected' : ''}`}
                aria-pressed={form.valueCapacity}
                onClick={toggleCapacity}
              >
                ⏱ Hours Added to Capacity
              </button>
              <button
                type="button"
                className={`value-type-btn ${form.valueBenefit ? 'selected' : ''}`}
                aria-pressed={form.valueBenefit}
                onClick={toggleBenefit}
              >
                ✦ Non-Time Benefit
              </button>
            </div>

            {form.valueCapacity && (
              <div className="value-type-body">
                <p className="field-hint">How much time did this free up?</p>
                <div className="time-input-row">
                  <div className="time-input-group">
                    <input type="number" name="timeSavedHrs" min="0" placeholder="0" value={form.timeSavedHrs} onChange={handleChange} />
                    <span className="time-unit">hrs</span>
                  </div>
                  <div className="time-input-group">
                    <input type="number" name="timeSavedMins" min="0" max="59" placeholder="0" value={form.timeSavedMins} onChange={handleChange} />
                    <span className="time-unit">min</span>
                  </div>
                </div>
              </div>
            )}

            {form.valueBenefit && (
              <div className="value-type-body">
                <p className="field-hint">Describe the benefit: improved quality, better insights, increased accuracy, etc.</p>
                <RichTextEditor
                  value={form.benefits}
                  onChange={(html) => setForm((f) => ({ ...f, benefits: html }))}
                  placeholder="Describe the benefit..."
                  rows={3}
                />
              </div>
            )}
          </div>

          <div className="form-group">
            <label>How Often Do You Use This AI Solution? *</label>
            <div className="radio-group">
              {FREQUENCIES.map((f) => (
                <label key={f} className={`radio-option ${form.frequency === f ? 'selected' : ''}`}>
                  <input type="radio" name="frequency" value={f} required checked={form.frequency === f} onChange={handleChange} />
                  {f}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Attachments</label>

            {existingAttachments.length > 0 && (
              <div className="attachment-edit-list">
                {existingAttachments.map((file) => (
                  <div key={file.name} className="attachment-edit-item">
                    {IMAGE_EXTS.test(file.name)
                      ? <img src={file.url} alt={file.name} className="attachment-edit-thumb" />
                      : <span className="attachment-edit-icon">📄</span>
                    }
                    <span className="attachment-edit-name">{file.name}</span>
                    <button
                      type="button"
                      className="attachment-remove-btn"
                      onClick={() => handleRemoveExisting(file)}
                      aria-label="Remove"
                    >✕</button>
                  </div>
                ))}
              </div>
            )}

            {newAttachments.length > 0 && (
              <div className="attachment-edit-list">
                {newAttachments.map((file, i) => (
                  <div key={i} className="attachment-edit-item attachment-edit-item--new">
                    <span className="attachment-edit-icon">📎</span>
                    <span className="attachment-edit-name">{file.name}</span>
                    <button
                      type="button"
                      className="attachment-remove-btn"
                      onClick={() => handleRemoveNew(i)}
                      aria-label="Remove"
                    >✕</button>
                  </div>
                ))}
              </div>
            )}

            <DropZone onFiles={(files) => setNewAttachments((prev) => [...prev, ...files])} />
          </div>

          {error && <div className="form-error">{error}</div>}

          <div className="edit-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}
